import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { useAccount } from '../context/AccountContext';
import client from '../api/client';
import { C, Btn, Input, Card, Toast, useNotify } from '../utils/styles';

const FIELD_TYPES = [
  { id:'text',        label:'Text',         icon:'ti-forms'        },
  { id:'email',       label:'Email',        icon:'ti-mail'         },
  { id:'number',      label:'Number',       icon:'ti-number'       },
  { id:'url',         label:'URL',          icon:'ti-link'         },
  { id:'date',        label:'Date',         icon:'ti-calendar'     },
  { id:'checkbox',    label:'Checkbox',     icon:'ti-checkbox'     },
  { id:'select',      label:'Dropdown',     icon:'ti-chevron-down' },
  { id:'multiselect', label:'Multi-select', icon:'ti-list-check'   },
  { id:'textarea',    label:'Text Area',    icon:'ti-align-left'   },
];

export default function Settings() {
  const { user, isSuperAdmin, isAdmin, isPlatformAdmin } = useAuth();
  const { brand, updateBrand } = useBrand();
  const { isSub, isAgency, isWhiteLabel, hasAI, activeAccount } = useAccount();
  const [activeTab,    setActiveTab]    = useState('account');
  const [notification, notify]          = useNotify();

  const TABS = [
    ...((isSub || (isAgency && !isWhiteLabel))
      ? [{ id:'account',  label:'Account',       icon:'ti-building'     }]
      : []),
    ...((!isSub && !(isAgency && !isWhiteLabel))
      ? [{ id:'brand',    label:'Branding',       icon:'ti-palette'      }]
      : []),
    { id:'fields',   label:'Custom Fields',  icon:'ti-forms'        },
    { id:'ai',       label:'AI',             icon:'ti-sparkles'     },
    { id:'profile',  label:'Profile',        icon:'ti-user-circle'  },
    ...(isSuperAdmin ? [{ id:'system', label:'System', icon:'ti-database' }] : []),
  ];

  // Branding
  const [localBrand,  setLocalBrand]  = useState({ ...brand });
  const [brandSaving, setBrandSaving] = useState(false);

  // Account
  const [acctName,   setAcctName]   = useState(activeAccount?.name || '');
  const [acctLogo,   setAcctLogo]   = useState(activeAccount?.logo_url || '');
  const [acctSaving, setAcctSaving] = useState(false);

  // Profile — controlled inputs
  const [profileName,     setProfileName]     = useState(user?.name || '');
  const [profileEmail,    setProfileEmail]    = useState(user?.email || '');
  const [profilePassword, setProfilePassword] = useState('');
  const [profileSaving,   setProfileSaving]   = useState(false);

  // Custom fields
  const [fields,       setFields]       = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [fieldSaving,  setFieldSaving]  = useState(false);
  const [newField,     setNewField]     = useState({ name:'', field_key:'', field_type:'text', placeholder:'', is_required:false, options:[] });
  const [newOption,    setNewOption]    = useState('');

  // AI
  const [aiStatus,   setAiStatus]   = useState({ configured:false, masked_key:null, updated_at:null });
  const [aiLoading,  setAiLoading]  = useState(true);
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [showAiKey,  setShowAiKey]  = useState(false);
  const [aiSaving,   setAiSaving]   = useState(false);
  const [aiRemoving, setAiRemoving] = useState(false);
  const [editingKey, setEditingKey] = useState(false);

  useEffect(() => { setLocalBrand({ ...brand }); }, [brand]);
  useEffect(() => {
    setAcctName(activeAccount?.name || '');
    setAcctLogo(activeAccount?.logo_url || '');
  }, [activeAccount]);
  useEffect(() => { setProfileName(user?.name || ''); setProfileEmail(user?.email || ''); }, [user]);
  useEffect(() => { fetchFields(); }, []); // eslint-disable-line
  useEffect(() => { if (isAdmin) fetchAiStatus(); }, []); // eslint-disable-line

  useEffect(() => {
    if (TABS.length > 0 && !TABS.find(t => t.id === activeTab)) {
      setActiveTab(TABS[0].id);
    }
  }, [isSub, isAgency, isWhiteLabel]); // eslint-disable-line

  const fetchFields = async () => {
    try { const { data } = await client.get('/api/contacts/custom-fields'); setFields(data||[]); } catch {}
  };

  const fetchAiStatus = async () => {
    setAiLoading(true);
    try { const { data } = await client.get('/api/settings/ai'); setAiStatus(data); }
    catch {}
    finally { setAiLoading(false); }
  };

  const handleSaveAccount = async () => {
    if (!acctName.trim()) return notify('Account name is required', 'error');
    setAcctSaving(true);
    try {
      await client.patch('/api/accounts/me', { name: acctName.trim(), logo_url: acctLogo });
      notify('Account updated!');
    } catch (err) { notify(err.response?.data?.error || 'Failed', 'error'); }
    finally { setAcctSaving(false); }
  };

  const handleSaveBrand = async () => {
    setBrandSaving(true);
    try {
      await client.post('/api/settings/brand', localBrand);
      updateBrand(localBrand);
      notify('Branding saved & applied!');
    } catch { notify('Failed to save branding', 'error'); }
    finally { setBrandSaving(false); }
  };

  // ── Profile save — wired to API ──────────────────────────────
  const handleSaveProfile = async () => {
    if (!profileName.trim()) return notify('Name is required', 'error');
    setProfileSaving(true);
    try {
      // Save name
      await client.patch('/api/settings/profile', { name: profileName.trim() });

      // Change password only if filled in
      if (profilePassword.trim()) {
        if (profilePassword.length < 8) {
          setProfileSaving(false);
          return notify('Password must be at least 8 characters', 'error');
        }
        await client.post('/api/auth/change-password', { password: profilePassword });
        setProfilePassword('');
      }

      notify('Profile updated successfully!');
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to save profile', 'error');
    } finally { setProfileSaving(false); }
  };

  const autoKey    = (name) => name.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'');
  const addOption  = () => { if (!newOption.trim()) return; setNewField({...newField, options:[...newField.options, newOption.trim()]}); setNewOption(''); };
  const removeOption = (i) => setNewField({...newField, options:newField.options.filter((_,idx) => idx !== i)});
  const needsOptions = ['select','multiselect','checkbox'].includes(newField.field_type);

  const handleAddField = async () => {
    if (!newField.name || !newField.field_key) return notify('Name and key required', 'error');
    setFieldSaving(true);
    try {
      await client.post('/api/contacts/custom-fields', newField);
      notify('Custom field added!');
      setShowAddField(false);
      setNewField({ name:'', field_key:'', field_type:'text', placeholder:'', is_required:false, options:[] });
      setNewOption('');
      fetchFields();
    } catch (err) { notify(err.response?.data?.error || 'Failed', 'error'); }
    finally { setFieldSaving(false); }
  };

  const handleDeleteField = async (id) => {
    if (!window.confirm('Delete this field? All data will be lost.')) return;
    try {
      await client.delete(`/api/contacts/custom-fields/${id}`);
      setFields(fields.filter(f => f.id !== id));
      notify('Field deleted');
    } catch { notify('Failed', 'error'); }
  };

  const handleSaveAiKey = async () => {
    if (!aiKeyInput.trim()) return notify('Please enter an API key', 'error');
    setAiSaving(true);
    try {
      const { data } = await client.post('/api/settings/ai', { anthropic_api_key: aiKeyInput.trim() });
      setAiStatus({ configured:true, masked_key:data.masked_key, updated_at:new Date().toISOString() });
      setAiKeyInput(''); setEditingKey(false); setShowAiKey(false);
      notify('API key saved! AI features are now active.');
    } catch (err) { notify(err.response?.data?.error || 'Failed', 'error'); }
    finally { setAiSaving(false); }
  };

  const handleRemoveAiKey = async () => {
    if (!window.confirm('Remove the AI key? AI features will stop working.')) return;
    setAiRemoving(true);
    try {
      await client.delete('/api/settings/ai');
      setAiStatus({ configured:false, masked_key:null, updated_at:null });
      notify('API key removed');
    } catch { notify('Failed', 'error'); }
    finally { setAiRemoving(false); }
  };

  const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box' };

  return (
    <div>
      <Toast {...notification} />

      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Settings</h1>
        <p style={{ fontSize:13, color:C.muted }}>Manage your CRM configuration</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:24, background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:6, flexWrap:'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex:1, minWidth:80, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 12px', borderRadius:8, border:'none', background:activeTab===t.id?C.primary:'transparent', color:activeTab===t.id?'#fff':C.muted, fontSize:12, fontWeight:activeTab===t.id?600:400, cursor:'pointer' }}>
            <i className={`ti ${t.icon}`} style={{ fontSize:15 }} />{t.label}
          </button>
        ))}
      </div>

      {/* ── Account tab ── */}
      {activeTab === 'account' && (
        <Card title="Account Settings" icon="ti-building">
          <p style={{ fontSize:13, color:C.muted, marginBottom:20 }}>
            Update your account name and the square logo that appears in the account switcher.
          </p>
          <div style={{ display:'grid', gap:14, marginBottom:20 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>Account Name *</label>
              <input style={inp} value={acctName} onChange={e => setAcctName(e.target.value)} placeholder="Your business name" />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>
                Switcher Logo URL <span style={{ fontSize:11, color:C.faint, fontWeight:400 }}>(square image, shows in account switcher)</span>
              </label>
              <input style={inp} value={acctLogo} onChange={e => setAcctLogo(e.target.value)} placeholder="https://your-domain.com/logo-square.png" />
              {acctLogo && (
                <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:10 }}>
                  <img src={acctLogo} alt="preview"
                    style={{ width:40, height:40, borderRadius:8, objectFit:'cover', border:`1px solid ${C.border}` }}
                    onError={e => { e.target.style.display='none'; }} />
                  <span style={{ fontSize:12, color:C.muted }}>Preview</span>
                </div>
              )}
              <div style={{ fontSize:11, color:C.faint, marginTop:6 }}>
                Use a square PNG or JPEG, minimum 64×64px.
              </div>
            </div>
          </div>
          <Btn onClick={handleSaveAccount} disabled={acctSaving}>
            <i className="ti ti-device-floppy" /> {acctSaving ? 'Saving...' : 'Save Account Settings'}
          </Btn>
        </Card>
      )}

      {/* ── Branding tab ── */}
      {activeTab === 'brand' && (
        <Card title="Branding & Appearance" icon="ti-palette">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>App Name <span style={{ color:C.danger }}>*</span></label>
              <input style={inp} placeholder="Your Agency Name" value={localBrand.app_name||''} onChange={e => setLocalBrand({...localBrand, app_name:e.target.value})} />
              {!localBrand.app_name?.trim() && <div style={{ fontSize:11, color:C.danger, marginTop:3 }}>App name is required</div>}
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>Tagline <span style={{ fontSize:11, color:C.faint, fontWeight:400 }}>(optional)</span></label>
              <input style={inp} placeholder="Your tagline" value={localBrand.tagline||''} onChange={e => setLocalBrand({...localBrand, tagline:e.target.value})} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            {[['Primary Color','primary_color'],['Sidebar / Dark Color','dark_color']].map(([label, key]) => (
              <div key={key}>
                <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:6, display:'block' }}>{label}</label>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={localBrand[key]||'#000000'} onChange={e => setLocalBrand({...localBrand,[key]:e.target.value})}
                    style={{ width:44, height:36, borderRadius:8, border:`1px solid ${C.border}`, cursor:'pointer', padding:2 }} />
                  <input style={{ flex:1, ...inp }} value={localBrand[key]||''} onChange={e => setLocalBrand({...localBrand,[key]:e.target.value})} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div>
              <Input label="Sidebar Logo URL" placeholder="https://your-domain.com/sidebar-logo.png" value={localBrand.logo_url||''} onChange={e => setLocalBrand({...localBrand,logo_url:e.target.value})} />
              <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>Shown in the CRM sidebar. Use PNG.</div>
            </div>
            <div>
              <Input label="Login Page Logo URL" placeholder="https://your-domain.com/login-logo.png" value={localBrand.login_logo_url||''} onChange={e => setLocalBrand({...localBrand,login_logo_url:e.target.value})} />
              <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>Falls back to sidebar logo if blank.</div>
            </div>
            <div>
              <Input label="Favicon URL" placeholder="https://your-domain.com/favicon.ico" value={localBrand.favicon_url||''} onChange={e => setLocalBrand({...localBrand,favicon_url:e.target.value})} />
              <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>Browser tab icon. Use .ico or 32×32 .png.</div>
            </div>
          </div>
          {localBrand.logo_url && (
            <div style={{ background:'#f9fafb', borderRadius:10, border:`1px solid ${C.border}`, padding:'12px 16px', marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:10 }}>Sidebar Logo Display Mode</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { id:false, label:'Logo + App Name',      desc:'Small logo beside app name',        icon:'ti-layout-sidebar' },
                  { id:true,  label:'Logo Only — Full Width', desc:'Logo fills sidebar width, hides text', icon:'ti-photo'          },
                ].map(opt => (
                  <button key={String(opt.id)} onClick={() => setLocalBrand({...localBrand, hide_name:opt.id})}
                    style={{ padding:'10px 12px', borderRadius:8, border:`1px solid ${localBrand.hide_name===opt.id?C.primary:C.border}`, background:localBrand.hide_name===opt.id?'var(--color-primary-light)':'#fff', cursor:'pointer', textAlign:'left' }}>
                    <i className={`ti ${opt.icon}`} style={{ fontSize:18, color:localBrand.hide_name===opt.id?C.primary:C.faint, display:'block', marginBottom:6 }} />
                    <div style={{ fontWeight:600, fontSize:12, color:localBrand.hide_name===opt.id?C.primary:C.dark }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:C.faint, marginTop:2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <Btn onClick={handleSaveBrand} disabled={brandSaving || !localBrand.app_name?.trim()}>
            <i className="ti ti-device-floppy" />{brandSaving ? 'Saving...' : 'Save & Apply Branding'}
          </Btn>
        </Card>
      )}

      {/* ── Custom Fields ── */}
      {activeTab === 'fields' && (
        <Card title="Custom Contact Fields" icon="ti-forms">
          <p style={{ fontSize:13, color:C.muted, marginBottom:16 }}>Define extra fields that appear on every contact.</p>
          <div style={{ marginBottom:16 }}>
            {fields.map((f,i) => (
              <div key={f.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:i%2===0?'#f9fafb':'#fff', borderRadius:8, marginBottom:4, border:'1px solid #f3f4f6' }}>
                <i className="ti ti-grip-vertical" style={{ color:'#d1d5db', fontSize:16 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:500, fontSize:13 }}>{f.name}</div>
                  <div style={{ fontSize:11, color:C.faint }}>
                    Key: <code style={{ background:'#f3f4f6', padding:'1px 5px', borderRadius:3 }}>{f.field_key}</code>
                    {' · '}Type: {f.field_type}{f.is_required && ' · Required'}{f.options?.length > 0 && ` · ${f.options.length} options`}
                  </div>
                </div>
                <span style={{ background:'var(--color-primary-light)', color:C.primary, borderRadius:6, padding:'2px 10px', fontSize:11, fontWeight:500 }}>
                  {FIELD_TYPES.find(t => t.id===f.field_type)?.label || f.field_type}
                </span>
                <button onClick={() => handleDeleteField(f.id)} style={{ padding:'4px 8px', borderRadius:6, border:'none', background:'#FEF2F2', color:C.danger, cursor:'pointer', fontSize:12 }}>
                  <i className="ti ti-trash" />
                </button>
              </div>
            ))}
            {!fields.length && <div style={{ textAlign:'center', padding:24, color:C.faint, fontSize:13 }}>No custom fields yet.</div>}
          </div>
          {showAddField ? (
            <div style={{ background:'#f9fafb', borderRadius:10, border:`1px solid ${C.border}`, padding:20 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:14, color:C.dark }}>New Custom Field</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                <Input label="Field Name"    placeholder="e.g. Address"  value={newField.name}        onChange={e => setNewField({...newField, name:e.target.value, field_key:autoKey(e.target.value)})} />
                <Input label="Field Key"     placeholder="e.g. address"  value={newField.field_key}   onChange={e => setNewField({...newField, field_key:e.target.value})} />
                <Input label="Placeholder"   placeholder="Hint text..."  value={newField.placeholder} onChange={e => setNewField({...newField, placeholder:e.target.value})} />
                <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:20 }}>
                  <input type="checkbox" id="req" checked={newField.is_required} onChange={e => setNewField({...newField, is_required:e.target.checked})} style={{ cursor:'pointer', accentColor:C.primary }} />
                  <label htmlFor="req" style={{ fontSize:13, color:C.text, cursor:'pointer' }}>Required field</label>
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:8, display:'block' }}>Field Type</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                  {FIELD_TYPES.map(t => (
                    <button key={t.id} onClick={() => setNewField({...newField, field_type:t.id, options:[]})}
                      style={{ padding:'8px 10px', borderRadius:8, border:`1px solid ${newField.field_type===t.id?C.primary:C.border}`, background:newField.field_type===t.id?'var(--color-primary-light)':'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:8, color:newField.field_type===t.id?C.primary:C.text, fontWeight:newField.field_type===t.id?600:400, fontSize:12 }}>
                      <i className={`ti ${t.icon}`} style={{ fontSize:15, color:newField.field_type===t.id?C.primary:C.faint }} />{t.label}
                    </button>
                  ))}
                </div>
              </div>
              {needsOptions && (
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:8, display:'block' }}>Options</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    {newField.options.map((opt,i) => (
                      <span key={i} style={{ background:'var(--color-primary-light)', border:'1px solid #bae6fd', color:C.primary, borderRadius:20, padding:'3px 10px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        {opt}<button onClick={() => removeOption(i)} style={{ background:'none', border:'none', cursor:'pointer', color:C.faint, padding:0, fontSize:12 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <input style={{ flex:1, ...inp }} placeholder="Type an option and press Enter..." value={newOption} onChange={e => setNewOption(e.target.value)} onKeyDown={e => e.key==='Enter' && addOption()} />
                    <Btn variant="outline" onClick={addOption}><i className="ti ti-plus" /> Add</Btn>
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <Btn onClick={handleAddField} disabled={fieldSaving}><i className="ti ti-plus" />{fieldSaving?'Adding...':'Add Field'}</Btn>
                <Btn variant="outline" onClick={() => { setShowAddField(false); setNewField({ name:'', field_key:'', field_type:'text', placeholder:'', is_required:false, options:[] }); }}>Cancel</Btn>
              </div>
            </div>
          ) : (
            <Btn onClick={() => setShowAddField(true)}><i className="ti ti-plus" /> Add Custom Field</Btn>
          )}
        </Card>
      )}

      {/* ── AI tab ── */}
      {activeTab === 'ai' && (
        <Card title="AI Configuration" icon="ti-sparkles">
          {!hasAI && !isPlatformAdmin ? (
            <div style={{ textAlign:'center', padding:'32px 20px' }}>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <i className="ti ti-sparkles" style={{ fontSize:28, color:'#9ca3af' }} />
              </div>
              <div style={{ fontWeight:700, fontSize:16, color:C.dark, marginBottom:8 }}>AI features not included in your plan</div>
              <p style={{ fontSize:13, color:C.muted, maxWidth:360, margin:'0 auto 20px' }}>
                Upgrade to the <strong>Agency</strong> or <strong>Agency Pro</strong> plan to unlock AI lead scoring, email drafts, pipeline insights, and workflow suggestions.
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, maxWidth:320, margin:'0 auto 24px' }}>
                {[{icon:'ti-target-arrow',label:'Lead Scoring'},{icon:'ti-mail-forward',label:'AI Email Drafts'},{icon:'ti-chart-line',label:'Pipeline Insights'},{icon:'ti-route',label:'Workflow Suggestions'}].map(f => (
                  <div key={f.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#f9fafb', borderRadius:8 }}>
                    <i className={`ti ${f.icon}`} style={{ fontSize:15, color:'#9ca3af' }} />
                    <span style={{ fontSize:12, color:'#9ca3af' }}>{f.label}</span>
                  </div>
                ))}
              </div>
              <Btn onClick={() => notify('Contact support@digitalxtension.com to upgrade your plan')}>
                <i className="ti ti-arrow-up-circle" /> Upgrade Plan
              </Btn>
            </div>
          ) : (
            <>
              <p style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Powers AI lead scoring, email drafting, pipeline insights, and workflow suggestions. Your API key is stored securely.</p>
              {aiLoading ? (
                <div style={{ padding:'24px 0', textAlign:'center', color:C.faint, fontSize:13 }}>
                  <i className="ti ti-loader ti-spin" style={{ fontSize:18, marginBottom:8, display:'block' }} />Checking AI configuration...
                </div>
              ) : aiStatus.configured && !editingKey ? (
                <div>
                  <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:16, marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                    <i className="ti ti-circle-check" style={{ fontSize:24, color:C.success }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13, color:'#166534' }}>API Key Configured</div>
                      <div style={{ fontSize:12, color:'#166534', marginTop:2 }}>Key: <code style={{ background:'#fff', padding:'1px 6px', borderRadius:4 }}>{aiStatus.masked_key}</code></div>
                      {aiStatus.updated_at && <div style={{ fontSize:11, color:'#16653480', marginTop:2 }}>Last updated {new Date(aiStatus.updated_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <Btn variant="outline" onClick={() => setEditingKey(true)}><i className="ti ti-edit" /> Update Key</Btn>
                    <Btn variant="outline" onClick={handleRemoveAiKey} disabled={aiRemoving} style={{ color:C.danger }}>
                      <i className={`ti ${aiRemoving?'ti-loader ti-spin':'ti-trash'}`} /> {aiRemoving?'Removing...':'Remove Key'}
                    </Btn>
                  </div>
                </div>
              ) : (
                <div>
                  {aiStatus.configured && <div style={{ marginBottom:14 }}><Btn variant="outline" onClick={() => { setEditingKey(false); setAiKeyInput(''); }}><i className="ti ti-arrow-left" /> Cancel</Btn></div>}
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:6, display:'block' }}>Anthropic API Key *</label>
                    <div style={{ position:'relative' }}>
                      <input type={showAiKey?'text':'password'} placeholder="sk-ant-..." value={aiKeyInput} onChange={e => setAiKeyInput(e.target.value)}
                        style={{ width:'100%', padding:'9px 38px 9px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'monospace' }} />
                      <button type="button" onClick={() => setShowAiKey(!showAiKey)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.faint, padding:0 }}>
                        <i className={`ti ${showAiKey?'ti-eye-off':'ti-eye'}`} style={{ fontSize:16 }} />
                      </button>
                    </div>
                    <div style={{ fontSize:11, color:C.faint, marginTop:6 }}>
                      Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" style={{ color:C.primary }}>console.anthropic.com <i className="ti ti-external-link" style={{ fontSize:11 }} /></a>
                    </div>
                  </div>
                  <Btn onClick={handleSaveAiKey} disabled={aiSaving || !aiKeyInput.trim()}>
                    <i className={`ti ${aiSaving?'ti-loader ti-spin':'ti-device-floppy'}`} /> {aiSaving?'Saving...':'Save API Key'}
                  </Btn>
                </div>
              )}
              <div style={{ marginTop:24, paddingTop:20, borderTop:`1px solid ${C.border}` }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:10 }}>Powers these features:</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {[{icon:'ti-target-arrow',label:'Lead Scoring'},{icon:'ti-mail-forward',label:'AI Email Drafts'},{icon:'ti-chart-line',label:'Pipeline Insights'},{icon:'ti-route',label:'Workflow Suggestions'}].map(f => (
                    <div key={f.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#f9fafb', borderRadius:8 }}>
                      <i className={`ti ${f.icon}`} style={{ fontSize:15, color:C.primary }} />
                      <span style={{ fontSize:12, color:C.text }}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
      )}

      {/* ── Profile — wired to API ── */}
      {activeTab === 'profile' && (
        <Card title="Profile" icon="ti-user-circle">
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
            <div style={{ width:52, height:52, borderRadius:'50%', background:C.primary, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:18, fontWeight:700 }}>
              {user?.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight:600, fontSize:15 }}>{user?.name}</div>
              <div style={{ fontSize:12, color:C.muted }}>{user?.email}</div>
              <span style={{ background:'var(--color-primary-light)', color:C.primary, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:500, marginTop:4, display:'inline-block' }}>
                {user?.role?.replace(/_/g,' ')}
              </span>
            </div>
          </div>
          <div style={{ display:'grid', gap:12, marginBottom:20 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>Full Name *</label>
              <input style={inp} value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="Your full name" />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>
                Email <span style={{ fontSize:11, color:C.faint, fontWeight:400 }}>(contact support to change email)</span>
              </label>
              <input style={{ ...inp, background:'#f9fafb', color:C.muted }} value={profileEmail} readOnly />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' }}>
                New Password <span style={{ fontSize:11, color:C.faint, fontWeight:400 }}>(leave blank to keep current)</span>
              </label>
              <input style={inp} type="password" placeholder="Min 8 characters" value={profilePassword} onChange={e => setProfilePassword(e.target.value)} />
            </div>
          </div>
          <Btn onClick={handleSaveProfile} disabled={profileSaving}>
            <i className={`ti ${profileSaving?'ti-loader ti-spin':'ti-device-floppy'}`} /> {profileSaving ? 'Saving...' : 'Save Changes'}
          </Btn>
        </Card>
      )}

      {/* ── System (superadmin only) ── */}
      {activeTab === 'system' && isSuperAdmin && (
        <Card title="System Information" icon="ti-database">
          <div style={{ display:'grid', gap:10, marginBottom:16 }}>
            <Input label="Supabase Project URL" defaultValue="https://bpwblamcbugpuqnfjcxh.supabase.co" readOnly />
            <Input label="Backend API"          defaultValue="https://api.digitalxtension.com" readOnly />
            <Input label="Frontend URL"         defaultValue="https://app.digitalxtension.com" readOnly />
          </div>
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#166534', display:'flex', alignItems:'center', gap:6 }}>
            <i className="ti ti-circle-check" style={{ fontSize:15 }} />All systems operational
          </div>
        </Card>
      )}
    </div>
  );
}
